import { useEffect, useMemo } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FacilityRequestsApi from 'libs/api/facility/requestsApi';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { useApi } from 'hooks/useApi';
import RequestsAdapter, { FormValuesAdapter } from './requests.adapter';
import { getShops, getInactiveShops } from 'hooks/useShops';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import moment from 'moment';

const api = new FacilityRequestsApi();

const statusColors = {
  status_confirmed: 'green',
  status_reject: 'red',
  status_refused: '#611a1a',
  status_pending: 'yellow',
  status_new: 'yellow',
  status_expired: 'black',
  //director statues

  director_approved_pending: 'yellow',
  director_approved_expired: 'black',
  director_approved_confirmed: 'green',
  director_approved_reject: 'red',
  director_approved_new: 'yellow',
};

const useRequests = (isMainPage = true, isEdit) => {
  let { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const {
    loading,
    data,
    onChangeTableFilter,
    handleDownloadExcel,
    setLoading,
    singleItem,
    setUpdateInProcess,
    onDelete,
    updateInProcess,
    setSingleItem,
    handleApprove,
  } = useApi({
    resourceKey: 'facility_requests',
    singleResourceKey: 'facility_request',
    api: api,
    isMainPage,
    adapter: RequestsAdapter,
  });

  const titleTypes = useMemo(
    () => ({
      extraordinary_opening: t('Apertura straordinaria'),
      extraordinary_closure: t('Chiusura Straordinaria'),
      inventory: t('Inventario'),
      extraordinary_cleaning: t('Pulizia straordinaria'),
      interior_set_up: t('Allestimento interno'),
      internal_meeting: t('Meeting interno'),
    }),
    [t],
  );

  const statusTexts = {
    status_confirmed: t('Confermato'),
    status_pending: t('In Attesa'),
    status_reject: t('Respinto'),
    status_refused: t('Rifiutato'),
    status_new: t('Nuovo'),
    status_expired: t('Expired'),
    //director texts
    director_approved_pending: t('In Attesa'),
    director_approved_confirmed: t('Confermato'),
    director_approved_reject: t('Respinto'),
    director_approved_expired: t('Expired'),
    director_approved_new: t('Nuovo'),
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => { },
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => { },
      },
      {
        title: t('Titolo'),
        dataIndex: 'type_event',
        sorter: () => { },
        render: (data) => titleTypes[data],
      },
      {
        title: t('Data richiesta'),
        dataIndex: 'created_at',
        sorter: () => { },
      },
      {
        title: t('Data inizio evento'),
        dataIndex: 'date_from',
        sorter: () => { },
        render: (data) => moment(data, 'YYYY-MM-DD').format('DD MMMM'),
      },
      {
        title: t('Data fine evento'),
        dataIndex: 'date_to',
        sorter: () => { },
        render: (data) => moment(data, 'YYYY-MM-DD').format('DD MMMM'),
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => { },
        render: (data, row) => (
          <Badge color={statusColors[data]} text={statusTexts[data] || 'Bob'} />
        ),
      },
      {
        title: t('Approvazione Direzione'),
        dataIndex: 'director_approved',
        sorter: () => { },
        render: (data, row) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'end'}
            onClick={(e) => e.stopPropagation()}
          >
            {(pages?.ability?.can_read !== false ||
              row.can_read !== false) && (
              <Col>
                <Link to={`${pathname}/${row.id}`}>
                  <Button type={'secondary'} icon={<EyeOutlined />} />
                </Link>
              </Col>
            )}
            {(row.can_update || row.can_approve) && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({ value: i.id, title: i.title })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'date',
        values: [],
        keys: ['by_date_from', 'by_date_to'],
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        values: shops,
        inactiveValues: inactiveShops,
      },
      {
        title: t('Titolo'),
        type: 'select',
        name: 'by_type_event',
        values: Object.keys(titleTypes).map((i) => ({
          value: i,
          title: titleTypes[i],
        })),
      },
      {
        title: t('Stato'),
        type: 'select',
        name: 'by_status',
        values: [
          { value: 'status_pending', title: t('Nuovo') },
          { value: 'status_refused', title: t('Rifiutato') },
          { value: 'status_confirmed', title: t('Confermato') },
          { value: 'status_reject', title: t('Respingi') },
        ],
      },
    ],
    [t, shoppingCenters, shops, inactiveShops, titleTypes],
  );

  const handleCreateOrUpdate = async (values) => {
    setLoading(true);
    try {
      const formData = FormValuesAdapter(values);
      if (!id) {
        await api.createRequest(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateRequest(id, formData);
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (id) => {
    setUpdateInProcess(true);
    try {
      await api.deleteFile(singleItem.id, id);
      setSingleItem((s) => ({
        ...s,
        docs: s.docs.filter((i) => i.id !== id),
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_from':
                case 'date_to': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key]),
                  });
                  break;
                }
                case 'time_from':
                case 'time_to': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'HH:mm'),
                  });
                  break;
                }
                case 'company': {
                  fieldsValues.push({
                    name: 'company_id',
                    value: singleItem[key]?.id,
                  });
                  break;
                }
                case 'docs': {
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem, form, isEdit, id]);

  return {
    columns,
    data,
    onChangeTableFilter,
    onDelete,
    loading,
    filters,
    form,
    shops,
    inactiveShops,
    titleTypes,
    handleDownloadExcel,
    handleCreateOrUpdate,
    singleItem,
    handleDeleteFile,
    updateInProcess,
    handleApprove,
    statusTexts,
  };
};

export default useRequests;
